import React, { useRef, useState } from "react";
import classNames from "classnames";

import Image from "../image";
import { Gabarit, Image as ImageType } from "@/types/image";
import { TYPE_GABARIT } from "../../utils/enums";
import SanitizeHTML from "../sanitizeHTML";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { useDebounce } from "../../hooks/useDebounce";

import Bouton, { VARIANT as boutonVARIANT, SIZE as boutonSIZE } from "../bouton";

import styles from "./figure.module.scss";

export const VARIANT = {
    DARK: "dark",
};

type VariantKeys = keyof typeof VARIANT;
type VariantValues = (typeof VARIANT)[VariantKeys];
type GabaritKeys = keyof typeof TYPE_GABARIT;
type GabaritValues = (typeof TYPE_GABARIT)[GabaritKeys];

type PropTypes = {
    ariaLabelledBy?: string;
    className?: string;
    dataTestid?: string;
    description?: string;
    gabarits: GabaritValues[];
    imageWidth?: number;
    images?: ImageType[];
    maxLinesDisplayed?: number;
    variant?: VariantValues;
};

const InImages = (current: Gabarit) => (img: ImageType) => img.typeGabarit === current;

const Figure = ({
    ariaLabelledBy,
    className = "",
    dataTestid = "mainFigure",
    description = "",
    gabarits,
    imageWidth,
    images,
    maxLinesDisplayed,
    variant = "",
}: PropTypes) => {
    const [clamped, setClamped] = useState(true);
    const [isReadMoreNeeded, setReadMoreNeeded] = useState(true);
    const figureRef = useRef<HTMLElement>(null);
    const figureCaptionContentRef = useRef<HTMLSpanElement>(null);

    const dataImages = images || [];
    // Cherche la première image qui match un gabarit, en ordre dans l'array de gabarit
    const firstGabaritFound = gabarits.findIndex((gabarit) => dataImages.find(InImages(gabarit)));
    const selectedImageData = dataImages.find(
        (img) => img.typeGabarit == gabarits[firstGabaritFound],
    );

    let descriptionImage = null;
    if (selectedImageData && typeof selectedImageData.description !== "undefined") {
        descriptionImage = selectedImageData.description;
    }

    const figureCaption = String(description || descriptionImage || "").trim();

    const handleReadCaptionClose = () => {
        setClamped(true);
    };

    useOnClickOutside([figureRef], handleReadCaptionClose);

    const checkTextClamped = () => {
        const elm = figureCaptionContentRef.current;
        if (elm) {
            setReadMoreNeeded(elm.scrollHeight > elm.clientHeight);
        }
    };
    // Check on component render
    useDebounce(checkTextClamped, 100, []);

    const onResizeEvent = () => {
        handleReadCaptionClose();
        setTimeout(() => {
            checkTextClamped();
        }, 300);
    };
    useWindowDimensions(onResizeEvent);

    if (!gabarits?.length) {
        return null;
    }

    return (
        <figure
            className={classNames(styles.me, {
                [className]: className,
                [styles[variant]]: variant && typeof styles[variant] !== "undefined",
            })}
            data-component="Figure"
            data-testid={dataTestid}
            ref={figureRef}
        >
            <Image alt={figureCaption} gabarits={gabarits} images={images} width={imageWidth} />
            {figureCaption && figureCaption !== "" && (
                <>
                    <figcaption
                        className={classNames(styles.figcaption, styles.figcaptionClamp)}
                        data-maxlinesdisplayed={maxLinesDisplayed}
                    >
                        <span
                            className={classNames(styles.figcaptionText, {
                                [styles.figcaptionTextClamped]: clamped,
                            })}
                            style={{ WebkitLineClamp: maxLinesDisplayed }}
                            ref={figureCaptionContentRef}
                        >
                            <SanitizeHTML html={figureCaption} tag="span" />
                        </span>
                        {isReadMoreNeeded && (
                            <Bouton
                                ariaLabel={ariaLabelledBy ? "" : "En lire plus"}
                                ariaLabelledBy={
                                    ariaLabelledBy ? `figure-read-more ${ariaLabelledBy}` : ""
                                }
                                className={classNames(
                                    styles.figcaptionClampButton,
                                    clamped ? styles.figcaptionClampButtonClamped : "",
                                )}
                                dataTestid="mainFigureReadMoreBtn"
                                id="figure-read-more"
                                onClick={() => setClamped(!clamped)}
                                size={boutonSIZE.XS}
                                variant={boutonVARIANT.BORDER}
                            >
                                {`Lire ${clamped ? "plus" : "moins"}`}
                            </Bouton>
                        )}
                    </figcaption>
                </>
            )}
        </figure>
    );
};

export default Figure;
